import React from 'react'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { PureQueryOptions } from 'apollo-client'
import { CellRenderer } from '../../legacy-design-system/grid'
import { Application, MetadataValue } from './shared'
import { Tag } from '../../legacy-design-system/tags'
import { ApplicationTooltipContent } from '../application-tooltip'
import { AppUnlicensedLink } from '../link'
import { PresenceIndicator } from '../presence-indicator'
import { ViolationIndicator } from './violation-indicator'
import { LoadingTextMask } from '../../legacy-design-system/loading-masks'
import { PrimaryApplicationClickableIndicator } from '../primary-application'
import { ApplicationImportance } from '../application-importance'
import { AgentFeature } from '../agent-features'
import { Role } from '@contrast-security-inc/base-types'
import { getApplicationDrilldownRoute } from '../route'
import { OverflowDetector } from '../../legacy-design-system/overflow-detector'
import { WithTooltip } from '../../design-system'

const MAX_TAGS = 20

const applicationMetadataCellCss = css`
  padding-left: 8px;
  padding-right: 20px;
  width: 100%;
  overflow: hidden;

  @media (max-width: 920px) {
    max-width: 300px;
  }

  @media (max-width: 768px) {
    max-width: 230px;
  }

  label: ApplicationMetaDataCell;
`

const applicationMetadataTopRow = css`
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
`

const presenceContainerCss = css`
  flex-grow: 0;
  margin-left: 8px;
`

const licensedContainerCss = css`
  flex-grow: 0;
  margin-left: 10px;
`

const primaryAppContainerCss = css`
  flex-grow: 0;
  margin-left: 4px;
  padding: 0 2px;
`

const languageContainerCss = css`
  flex-grow: 1;
  margin-top: 1px;
  color: ${theme.color.neutral60};
  display: inline-block;
  font-size: 12px;
`

const applicationNameContainerCss = css`
  flex-shrink: 0;
`

const licensedAppNameWithTagsAndTooltipCss = cx(
  applicationNameContainerCss,
  css`
    max-width: calc(90% - 80px);

    @media (max-width: 768px) {
      max-width: calc(90% - 85px);
    }

    @media (max-width: 650px) {
      max-width: calc(90% - 95px);
    }

    @media (max-width: 500px) {
      max-width: calc(90% - 100px);
    }
  `
)

const licensedAppNameWithTooltipCss = cx(
  applicationNameContainerCss,
  css`
    max-width: calc(100% - 80px);

    @media (max-width: 768px) {
      max-width: calc(100% - 85px);
    }

    @media (max-width: 650px) {
      max-width: calc(100% - 95px);
    }

    @media (max-width: 500px) {
      max-width: calc(100% - 100px);
    }
  `
)

const unlicensedAppNameWithTooltipCss = cx(
  applicationNameContainerCss,
  css`
    max-width: calc(100% - 140px);

    @media (max-width: 768px) {
      max-width: calc(100% - 145px);
    }

    @media (max-width: 700px) {
      max-width: calc(100% - 155px);
    }

    @media (max-width: 500px) {
      max-width: calc(100% - 160px);
    }
  `
)

const noWrapSpaceCss = css`
  white-space: nowrap;
`

const violationIndicatorContainerCss = css`
  margin-left: 4px;
`

const tagsContainerCss = css`
  display: flex;
  margin-left: 4px;
`

interface GetRendererProps {
  readonly organizationID: string
  readonly organizationRole: Role
  readonly isFreemiumOrganization: boolean
  readonly features?: AgentFeature[]
  readonly hasLicenses: boolean
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
}

type Props = CellRenderer<Application & { readonly organizationID: string }>

function getMetadataValues(customMetadataFields?: MetadataValue[]) {
  if (!customMetadataFields) {
    return ''
  }

  const values: string[] = customMetadataFields
    .filter((field) => field.unique)
    .map((field) => {
      if (field.subfields && field.subfields.length) {
        const subvalues: string[] = field.subfields
          .filter((field) => field.unique)
          .map((field) => field.value)
        if (subvalues.length) {
          return subvalues.join(' | ')
        }
      }
      return field.value
    })

  // eslint-disable-next-line unicorn/prefer-ternary
  if (values.length) {
    return ' | ' + values.join(' | ')
  } else {
    return ''
  }
}

export function getApplicationMetadataCellRenderer({
  organizationID,
  organizationRole,
  isFreemiumOrganization,
  features,
  hasLicenses,
  refetchQueries,
  refresh
}: GetRendererProps): Props {
  return function applicationMetadataCellRenderer({
    rowData: {
      id,
      name,
      role,
      language,
      license,
      url,
      importance,
      technologies,
      online,
      lastActivity,
      moduleCount,
      primary,
      violatedCompliancePolicies,
      archived,
      tags,
      customMetadataFields
    },
    getCellContentProps
  }) {
    const hasViolation =
      violatedCompliancePolicies.length > 0 && !isFreemiumOrganization
    const presenceContainerProps = getCellContentProps({
      className: presenceContainerCss
    })

    const languageContentProps = getCellContentProps({
      className: languageContainerCss
    })

    const primaryAppContainerProps = getCellContentProps({
      className: primaryAppContainerCss
    })

    const violationIndicatorContainerProps = getCellContentProps({
      className: violationIndicatorContainerCss
    })

    const licensedContainerProps = getCellContentProps({
      className: licensedContainerCss
    })

    const tagsContainerProps = getCellContentProps({
      className: tagsContainerCss
    })

    const appNameProps = license
      ? tags.length > 0
        ? getCellContentProps({
            className: licensedAppNameWithTagsAndTooltipCss
          })
        : getCellContentProps({
            className: licensedAppNameWithTooltipCss
          })
      : getCellContentProps({
          className: unlicensedAppNameWithTooltipCss
        })

    return (
      <div className={applicationMetadataCellCss}>
        <div className={applicationMetadataTopRow}>
          <div {...appNameProps}>
            <AppNameWithTooltip
              organizationID={organizationID}
              name={name}
              url={url}
              importance={importance}
              technologies={technologies}
              hasViolation={hasViolation}
              archived={archived}
              id={id}
              metadata={customMetadataFields}
            />
          </div>

          <div data-e2e="app-presence-indicator" {...presenceContainerProps}>
            <PresenceIndicator
              online={online}
              lastActivity={Number.parseInt(lastActivity, 10)}
            />
          </div>

          {primary && !isFreemiumOrganization && (
            <div data-e2e="primary-app-icon" {...primaryAppContainerProps}>
              <PrimaryApplicationClickableIndicator
                organizationID={organizationID}
                organizationRole={organizationRole}
                primaryApplication={{ id, name, licensed: !!license, role }}
                moduleCount={moduleCount}
                refetchQueries={refetchQueries}
                refresh={refresh}
              />
            </div>
          )}

          {hasViolation && (
            <div {...violationIndicatorContainerProps}>
              <ViolationIndicator
                organizationID={organizationID}
                organizationRole={organizationRole}
                violatedCompliancePolicies={violatedCompliancePolicies}
              />
            </div>
          )}

          {!license && !isFreemiumOrganization && (
            <div {...licensedContainerProps}>
              <AppUnlicensedLink
                role={organizationRole}
                organizationID={organizationID}
                applicationID={id}
                applicationName={name}
                applicationLanguage={language}
                features={features}
                archived={archived}
                hasLicenses={hasLicenses}
                refetchQueries={refetchQueries}
                refresh={refresh}
              />
            </div>
          )}

          {tags.length > 0 && (
            <div {...tagsContainerProps}>
              {[...tags]
                .slice(0, MAX_TAGS)
                .sort()
                .map((t, i) => (
                  <Tag key={i}>{t}</Tag>
                ))}
              {tags.length > MAX_TAGS ? (
                <Tag>{`+${tags.length - MAX_TAGS}`}</Tag>
              ) : null}
            </div>
          )}
        </div>
        <div {...languageContentProps}>
          {language.getDisplay()}
          {getMetadataValues(customMetadataFields)}
        </div>
      </div>
    )
  }
}

const applicationMetadataLoadingCellCss = cx(
  applicationMetadataCellCss,
  css`
    width: 100%;
  `
)

const appContainerLoadingCss = css`
  flex-grow: 1;
  width: 100%;
`

const appNameContainerLoadingCss = cx(
  applicationMetadataTopRow,
  css`
    display: flex;
    flex-direction: column;
    width: 60%;
  `
)

const maskedAppNameCss = css`
  width: 100%;
  padding: 1px 0px;
`

const maskedAppLanguageCss = cx(
  maskedAppNameCss,
  css`
    font-size: 0.6em;
  `
)

export function loadingApplicationMetadataCell() {
  return (
    <div className={applicationMetadataLoadingCellCss}>
      <div className={appContainerLoadingCss}>
        <div className={appNameContainerLoadingCss}>
          <div className={maskedAppNameCss}>
            <LoadingTextMask min={7} />
          </div>
          <div className={maskedAppLanguageCss}>
            <LoadingTextMask min={3} max={4} />
          </div>
        </div>
      </div>
    </div>
  )
}

const appNameTargetContainerCss = css`
  flex-shrink: 0;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  line-height: 1.2em;
  white-space: nowrap;
`

const appNameTextCss = cx(
  noWrapSpaceCss,
  css`
    color: ${theme.color.neutral100};
    cursor: pointer;

    &:hover {
      color: ${theme.color.neutral100};
      text-decoration: underline;
    }
  `
)

const violationCss = css`
  color: ${theme.color.red50};

  &:hover {
    color: ${theme.color.red50};
  }
`

const appNameTargetContainerViolationCss = cx(
  appNameTargetContainerCss,
  violationCss
)

const appNameViolationTextCss = cx(appNameTextCss, violationCss)

interface AppNameWithTooltipProps {
  readonly organizationID: string
  readonly name: string
  readonly url: string
  readonly importance: ApplicationImportance
  readonly technologies: string[]
  readonly hasViolation: boolean
  readonly archived: boolean
  readonly id: string
  readonly metadata?: MetadataValue[]
}

interface AppNameWithTooltipState {
  readonly overflowing: boolean
}

class AppNameWithTooltip extends React.Component<
  AppNameWithTooltipProps,
  AppNameWithTooltipState
> {
  public constructor(props) {
    super(props)
    this.state = {
      overflowing: false
    }
  }
  public render() {
    const {
      organizationID,
      name,
      url,
      importance,
      technologies,
      hasViolation,
      archived,
      id,
      metadata
    } = this.props

    const { overflowing } = this.state

    return (
      <OverflowDetector>
        {({ doesOverflowParent, registerElement }) => {
          return (
            <WithTooltip
              renderTooltipContent={() => {
                return (
                  <ApplicationTooltipContent
                    name={overflowing ? name : undefined}
                    url={url}
                    importance={importance}
                    technologies={technologies}
                    metadata={metadata}
                  />
                )
              }}
              children={({ ref, onMouseLeave, onMouseEnter }) => (
                <span
                  ref={ref}
                  className={
                    hasViolation
                      ? appNameTargetContainerViolationCss
                      : appNameTargetContainerCss
                  }
                  onMouseEnter={() => {
                    this.updateOverflowing(doesOverflowParent())
                    if (onMouseEnter) {
                      onMouseEnter()
                    }
                  }}
                  onMouseLeave={onMouseLeave}
                >
                  {archived ? (
                    <span ref={registerElement} className={noWrapSpaceCss}>
                      {name}
                    </span>
                  ) : (
                    <a
                      data-e2e={`application-name-cell-${name}`}
                      data-testid={'application-name-row'}
                      ref={registerElement}
                      className={
                        hasViolation ? appNameViolationTextCss : appNameTextCss
                      }
                      href={getApplicationDrilldownRoute(organizationID, id)}
                    >
                      {name}
                    </a>
                  )}
                </span>
              )}
            />
          )
        }}
      </OverflowDetector>
    )
  }

  private updateOverflowing = (overflowing: boolean) =>
    this.setState({ overflowing })
}
